import React, { useEffect, useMemo, useState, useRef } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ScrollView, Platform, Dimensions, ActivityIndicator, Image } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../utils/supabaseClient';

export default function MyProfileScreen() {
  const { colors } = useTheme();
  const { session, profile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const screenWidth = Dimensions.get('window').width;
  const isMobile = screenWidth < 768;

  const [form, setForm] = useState({
    // Personal
    name: '',
    email: '',
    phone: '',
    // Business
    business_name: '',
    business_email: '',
    gstin: '',
    pan: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'India',
    logo_url: '',
    // Payments
    bank_name: '',
    account_no: '',
    ifsc: '',
    upi_id: '',
    // Defaults
    invoice_prefix: '',
    default_currency: 'INR',
    default_gst_type: 'CGST_SGST',
  });

  useEffect(() => {
    // Hydrate form from existing profile data when available
    if (profile) {
      setForm((prev) => ({
        ...prev,
        name: profile.name || prev.name,
        email: profile.email || prev.email,
        phone: profile.phone || prev.phone,
        business_name: profile.business_name || profile.company_name || prev.business_name,
        business_email: profile.business_email || prev.business_email,
        gstin: profile.gstin || prev.gstin,
        pan: profile.pan || prev.pan,
        address_line1: profile.address_line1 || profile.address || prev.address_line1,
        address_line2: profile.address_line2 || prev.address_line2,
        city: profile.city || prev.city,
        state: profile.state || prev.state,
        postal_code: profile.postal_code || prev.postal_code,
        country: profile.country || prev.country,
        logo_url: profile.logo_url || prev.logo_url,
        bank_name: profile.bank_name || prev.bank_name,
        account_no: profile.account_no || prev.account_no,
        ifsc: profile.ifsc || prev.ifsc,
        upi_id: profile.upi_id || prev.upi_id,
        invoice_prefix: profile.invoice_prefix || prev.invoice_prefix,
        default_currency: profile.default_currency || prev.default_currency,
        default_gst_type: profile.default_gst_type || prev.default_gst_type,
      }));
    }
  }, [profile]);

  // Fast local cache hydration on web (in case network/auth is slow)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const uid = session?.user?.id;
    if (!uid) return;
    try {
      const key = `profile_full_${uid}`;
      const raw = window.localStorage.getItem(key);
      if (raw) {
        const cached = JSON.parse(raw);
        const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== '';
        setForm((prev) => {
          const next = { ...prev };
          Object.keys(cached || {}).forEach((k) => {
            const v = cached[k];
            if (isFilled(v)) next[k] = v;
          });
          return next;
        });
      }
    } catch (_) {}
  }, [session?.user?.id]);

  // Explicit fetch to ensure hydration even if AuthContext hasn't loaded yet
  useEffect(() => {
    (async () => {
      if (!session?.user?.id) return;
      try {
        const { data } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        if (data) {
          setForm((prev) => ({
            ...prev,
            name: data.name ?? prev.name,
            email: data.email ?? prev.email,
            phone: data.phone ?? prev.phone,
            business_name: data.business_name ?? data.company_name ?? prev.business_name,
            business_email: data.business_email ?? prev.business_email,
            gstin: data.gstin ?? prev.gstin,
            pan: data.pan ?? prev.pan,
            address_line1: data.address_line1 ?? data.address ?? prev.address_line1,
            address_line2: data.address_line2 ?? prev.address_line2,
            city: data.city ?? prev.city,
            state: data.state ?? prev.state,
            postal_code: data.postal_code ?? prev.postal_code,
            country: data.country ?? prev.country,
            logo_url: data.logo_url ?? prev.logo_url,
            bank_name: data.bank_name ?? prev.bank_name,
            account_no: data.account_no ?? prev.account_no,
            ifsc: data.ifsc ?? prev.ifsc,
            upi_id: data.upi_id ?? prev.upi_id,
            invoice_prefix: data.invoice_prefix ?? prev.invoice_prefix,
            default_currency: data.default_currency ?? prev.default_currency,
            default_gst_type: data.default_gst_type ?? prev.default_gst_type,
          }));
        }
      } catch (_) {}
    })();
  }, [session?.user?.id]);

  const styles = useMemo(() => StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    content: { padding: 24, width: '100%' },
    pageTitle: { fontSize: 22, fontWeight: '700', color: colors.text, marginBottom: 16 },
    card: { backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border, borderRadius: 12, padding: 16, marginBottom: 16 },
    cardTitle: { fontSize: 16, fontWeight: '700', color: colors.text, marginBottom: 12 },
    row: { flexDirection: isMobile ? 'column' : 'row', gap: 12, flexWrap: 'wrap' },
    gridRow: { flexDirection: isMobile ? 'column' : 'row', gap: 12, alignItems: 'stretch' },
    gridCol: { flex: 1, minWidth: 280 },
    col: { flex: 1, minWidth: 280 },
    label: { fontSize: 12, color: colors.textSecondary, marginBottom: 6 },
    input: { borderWidth: 1, borderColor: colors.border, borderRadius: 8, paddingHorizontal: 12, paddingVertical: Platform.OS === 'web' ? 10 : 8, color: colors.text, backgroundColor: colors.background, fontSize: 14 },
    actions: { flexDirection: 'row', gap: 8, alignItems: 'center', justifyContent: 'flex-end', marginTop: 12 },
    saveBtn: { backgroundColor: colors.primary, paddingHorizontal: 14, paddingVertical: 10, borderRadius: 8 },
    saveText: { color: '#fff', fontWeight: '600' },
    msg: { fontSize: 12, color: colors.textSecondary },
    savingText: { color: '#fff', fontWeight: '600', fontSize: 12 },
    logoActions: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    logoPreview: { width: 40, height: 40, resizeMode: 'contain', borderRadius: 6, borderWidth: 1, borderColor: colors.border },
  }), [colors, isMobile]);

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  // Helper: fetch with timeout + optional JSON parsing
  const fetchJsonWithTimeout = async (url, options = {}, timeoutMs = 8000) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const resp = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timer);
      let data = null;
      try {
        const text = await resp.text();
        data = text ? JSON.parse(text) : null;
      } catch (_) {}
      return { ok: resp.ok, status: resp.status, data };
    } catch (e) {
      clearTimeout(timer);
      return { ok: false, status: e?.name === 'AbortError' ? 'timeout' : 'error', data: null, error: e };
    }
  };

  // Helper: wrap any promise with a timeout so UI never hangs
  const withTimeout = (promise, ms = 5000) => {
    return Promise.race([
      promise,
      new Promise((resolve) => setTimeout(() => resolve({ data: null, error: { message: 'Timed out', code: 'ETIMEOUT' } }), ms)),
    ]);
  };

  const onSave = async (overrides = {}) => {
    if (!session?.user?.id) {
      setMessage('Please login to save your profile.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      // Read current DB row to merge and avoid clearing values with blanks
      let existing = null;
      try {
        const sel = supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        const { data } = await withTimeout(sel, 4000);
        existing = data || null;
      } catch (_) {}

      // Helper to include only non-empty values (preserve existing when blank)
      const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== '';
      const userMeta = session?.user?.user_metadata || {};
      let payload = {
        id: session.user.id,
        name: isFilled(form.name)
          ? form.name
          : (existing?.name ?? userMeta.name ?? (session.user.email ? session.user.email.split('@')[0] : 'User')),
        email: isFilled(form.email)
          ? form.email
          : (existing?.email ?? session.user.email ?? 'user@example.com'),
        phone: isFilled(form.phone) ? form.phone : (existing?.phone ?? 'EMPTY'),
      };
      const optionalKeys = [
        'business_name','business_email','gstin','pan','address_line1','address_line2','city','state','postal_code','country','logo_url','bank_name','account_no','ifsc','upi_id','invoice_prefix','default_currency','default_gst_type'
      ];
      optionalKeys.forEach((k) => {
        const v = form[k];
        if (isFilled(v)) payload[k] = v;
        else if (existing && isFilled(existing[k])) payload[k] = existing[k];
      });
      // Apply explicit overrides last to ensure values like logo_url are persisted immediately
      if (overrides && typeof overrides === 'object') {
        payload = { ...payload, ...overrides };
      }
      // Maintain company_name as mirror of business_name when provided/exists
      if (isFilled(form.business_name)) payload.company_name = form.business_name;
      else if (existing && isFilled(existing.company_name)) payload.company_name = existing.company_name;
      // Robust path: use REST API with the current user's JWT.
      const token = session?.access_token || session?.accessToken || null;
      if (!token) throw new Error('No session token — please login again.');
      let saveMessage = '';
      let refreshed = null;
      const baseHeaders = {
        apikey: SUPABASE_ANON_KEY,
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      // Try PATCH first; if the row does not exist, fall back to POST upsert
      const urlPatch = `${SUPABASE_URL}/rest/v1/profiles?id=eq.${session.user.id}`;
      const headersPatch = { ...baseHeaders, Prefer: 'return=representation' };
      const respPatch = await fetchJsonWithTimeout(urlPatch, { method: 'PATCH', headers: headersPatch, body: JSON.stringify(payload) });
      if (!respPatch.ok || !Array.isArray(respPatch.data) || respPatch.data.length === 0) {
        if (respPatch.status === 401 || respPatch.status === 403) {
          throw new Error('Update blocked by RLS or auth (401/403).');
        }
        const urlPost = `${SUPABASE_URL}/rest/v1/profiles`;
        const headersPost = { ...baseHeaders, Prefer: 'resolution=merge-duplicates,return=representation' };
        const respPost = await fetchJsonWithTimeout(urlPost, { method: 'POST', headers: headersPost, body: JSON.stringify(payload) });
        if (!respPost.ok) {
          if (respPost.status === 401 || respPost.status === 403) {
            throw new Error('Insert blocked by RLS or auth (401/403).');
          }
          throw new Error(`Save failed (${respPost.status}).`);
        }
        const dataPost = respPost.data;
        if (Array.isArray(dataPost) && dataPost.length > 0) {
          refreshed = dataPost[0];
          saveMessage = 'Saved via REST POST upsert';
        }
      } else {
        const dataPatch = respPatch.data;
        refreshed = Array.isArray(dataPatch) && dataPatch.length > 0 ? dataPatch[0] : null;
        saveMessage = refreshed ? 'Saved via REST PATCH' : 'Profile updated.';
      }
      // Re-fetch to confirm hydration only if needed
      if (!refreshed) {
        try {
          const sel = supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();
          const { data } = await withTimeout(sel, 4000);
          refreshed = data || null;
        } catch (_) {}
      }
      if (refreshed) {
        setForm((prev) => ({ ...prev, ...refreshed }));
      }
      // Cache the complete profile locally for invoice prefill
      try {
        if (Platform.OS === 'web') {
          const key = `profile_full_${session.user.id}`;
          const nextCache = refreshed || payload;
          window.localStorage.setItem(key, JSON.stringify(nextCache));
        }
      } catch (_) {}
      setMessage(saveMessage || 'Profile saved successfully.');
    } catch (e) {
      console.error('Profile save error:', e);
      const msg = typeof e?.message === 'string' ? e.message : 'Failed to save profile.';
      // Ignore dev overlay cross-origin noise in local web development
      const isDevOverlayNoise = msg.includes('toJSON') || msg.includes('cross-origin frame');
      if (!isDevOverlayNoise) {
        // Translate common errors for clarity
        const friendly =
          msg.includes('RLS') || msg.includes('401') || msg.includes('403')
            ? 'Save blocked by Row Level Security. Add update/insert policies for own rows.'
            : msg.includes('timed out') || msg.includes('Timeout')
            ? 'Network timed out. Check ad-blockers/VPN and retry.'
            : msg;
        setMessage(friendly);
      }
    } finally {
      setSaving(false);
    }
  };

  // Logo upload for web using Supabase Storage (fallback to local URL if bucket missing)
  const fileInputRef = useRef(null);
  const onUploadLogoPress = () => {
    if (Platform.OS === 'web') {
      try { fileInputRef.current?.click?.(); } catch (_) {}
    } else {
      setMessage('Logo upload is currently supported on web.');
    }
  };
  const handleLogoFile = async (file) => {
    if (!file) return;
    if (!session?.user?.id) {
      setMessage('Please login before uploading a logo.');
      return;
    }
    try {
      setMessage('Uploading logo…');
      setSaving(true);
      const ext = (file.name || 'logo').split('.').pop();
      const path = `user-logos/${session.user.id}_${Date.now()}.${ext || 'png'}`;
      let usedBucket = 'logos';
      let resp = await withTimeout(
        supabase.storage.from(usedBucket).upload(path, file, { upsert: true, contentType: file.type }),
        10000
      );
      if (resp?.error) {
        usedBucket = 'public';
        resp = await withTimeout(
          supabase.storage.from(usedBucket).upload(path, file, { upsert: true, contentType: file.type }),
          10000
        );
      }
      if (resp?.error) {
        const msg = (resp.error?.message || '').toLowerCase();
        const blocked = msg.includes('permission') || msg.includes('policy') || msg.includes('jwt') || msg.includes('401') || msg.includes('403');
        const localUrl = URL.createObjectURL(file);
        setField('logo_url', localUrl);
        setMessage(blocked
          ? 'Upload blocked by Storage policy (401/403). Using local preview only.'
          : (msg.includes('timeout') ? 'Upload timed out. Using local preview only.' : 'Logo set locally. Create a public Storage bucket "logos" or "public".'));
        setSaving(false);
        return;
      }
      const pub = supabase.storage.from(usedBucket).getPublicUrl(path);
      const publicUrl = pub?.data?.publicUrl || '';
      setField('logo_url', publicUrl);
      setMessage('Logo uploaded successfully. Saving to profile…');
      // Give state a moment to update then persist immediately
      await new Promise((r) => setTimeout(r, 50));
      // Ensure save cannot hang indefinitely
      await Promise.race([onSave({ logo_url: publicUrl }), new Promise((r) => setTimeout(r, 6000))]);
    } catch (e) {
      setMessage(e?.message || 'Logo upload failed.');
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (key, label, placeholder) => (
    <View style={{ marginBottom: 10 }}>
      <Text style={styles.label}>{label}</Text>
      <TextInput value={form[key] || ''} onChangeText={(v) => setField(key, v)} style={styles.input} placeholder={placeholder} autoCapitalize="none" autoCorrect={false} spellCheck={false} />
    </View>
  );

  return (
    <View style={styles.container}>
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <Text style={styles.pageTitle}>My Profile</Text>

        {/* Motivation banner */}
        <View style={[styles.card, { backgroundColor: '#F0F9FF', borderColor: '#BAE6FD' }]}> 
          <Text style={[styles.cardTitle, { color: '#0C4A6E', marginBottom: 6 }]}>Boost your invoicing speed</Text>
          <Text style={{ fontSize: 12, color: '#0C4A6E' }}>
            Complete your profile and we’ll automatically pre-fill your invoice “Billed By” details — business name, GSTIN/PAN, address,
            email, payment info and logo. Saving your profile helps you create professional invoices faster.
          </Text>
        </View>

        <View style={styles.gridRow}>
          <View style={[styles.card, styles.gridCol]}> 
            <Text style={styles.cardTitle}>Personal Details</Text>
            <View style={styles.row}>
              <View style={styles.col}>{renderInput('name', 'Full Name', 'Your name')}</View>
              <View style={styles.col}>{renderInput('email', 'Email', 'you@example.com')}</View>
            </View>
            {renderInput('phone', 'Phone', '9876543210')}
          </View>

          <View style={[styles.card, styles.gridCol]}>
            <Text style={styles.cardTitle}>Business Details</Text>
            <View style={styles.row}>
              <View style={styles.col}>{renderInput('business_name', 'Business Name', 'Your Business')}</View>
              <View style={styles.col}>{renderInput('business_email', 'Business Email', 'billing@yourbiz.com')}</View>
            </View>
            <View style={styles.row}>
              <View style={styles.col}>{renderInput('gstin', 'GSTIN', '22AAAAA0000A1Z5')}</View>
              <View style={styles.col}>{renderInput('pan', 'PAN', 'ABCDE1234F')}</View>
            </View>
            <View style={styles.row}>
              <View style={styles.col}>{renderInput('address_line1', 'Address Line 1', 'Street, Area')}</View>
              <View style={styles.col}>{renderInput('address_line2', 'Address Line 2', 'Optional')}</View>
            </View>
            <View style={styles.row}>
              <View style={styles.col}>{renderInput('city', 'City', 'Mumbai')}</View>
              <View style={styles.col}>{renderInput('state', 'State', 'Maharashtra')}</View>
            </View>
            <View style={styles.row}>
              <View style={styles.col}>{renderInput('postal_code', 'Postal Code', '400001')}</View>
              <View style={styles.col}>{renderInput('country', 'Country', 'India')}</View>
            </View>
            <View style={styles.row}>
              <View style={styles.col}>{renderInput('logo_url', 'Logo URL', 'https://.../logo.png')}</View>
              <View style={[styles.logoActions, { alignItems: 'center' }]}> 
                <TouchableOpacity style={[styles.saveBtn, { backgroundColor: colors.secondary || '#64748b' }]} onPress={onUploadLogoPress}>
                  <Text style={styles.saveText}>Upload Logo</Text>
                </TouchableOpacity>
                {form.logo_url ? (
                  <Image source={{ uri: form.logo_url }} style={styles.logoPreview} />
                ) : null}
                {Platform.OS === 'web' && (
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleLogoFile(e.target.files?.[0])}
                  />
                )}
              </View>
            </View>
          </View>
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Payment Details</Text>
          <View style={styles.row}>
            <View style={styles.col}>{renderInput('bank_name', 'Bank Name', 'HDFC Bank')}</View>
            <View style={styles.col}>{renderInput('account_no', 'Account Number', '1234567890')}</View>
          </View>
          <View style={styles.row}>
            <View style={styles.col}>{renderInput('ifsc', 'IFSC', 'HDFC0001234')}</View>
            <View style={styles.col}>{renderInput('upi_id', 'UPI ID', 'yourname@bank')}</View>
          </View>
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Invoice Defaults</Text>
          <View style={styles.row}>
            <View style={styles.col}>{renderInput('invoice_prefix', 'Invoice Prefix', 'INV')}</View>
            <View style={styles.col}>{renderInput('default_currency', 'Default Currency', 'INR')}</View>
          </View>
          {renderInput('default_gst_type', 'Default GST Type', 'CGST_SGST or IGST')}
          <View style={styles.actions}>
            {!!message && (<Text style={styles.msg}>{message}</Text>)}
            <TouchableOpacity disabled={saving} style={[styles.saveBtn, saving && { opacity: 0.9 }]} onPress={onSave}>
              {saving ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <ActivityIndicator size="small" color="#fff" />
                  <Text style={styles.saveText}>Saving…</Text>
                </View>
              ) : (
                <Text style={styles.saveText}>Save</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}